import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import useReducedMotion from "../hooks/useReducedMotion";

type HeroConstellationProps = {
  className?: string;
  density?: "low" | "medium";
  minWidth?: number;
  motionScale?: number;
  opacity?: number;
};

type BaseNode = {
  x: number;
  y: number;
  baseX: number;
  baseY: number;
  vx: number;
  vy: number;
  alpha: number;
  rotation: number;
  rotationSpeed: number;
  collisionRadius: number;
};

type PointNode = BaseNode & {
  kind: "point";
  radius: number;
};

type LabelNode = BaseNode & {
  kind: "label";
  label: string;
  fontSize: number;
};

type GraphNode = PointNode | LabelNode;

type ClusterTemplate = {
  anchorX: number;
  anchorY: number;
  pointOffsets: Array<[number, number]>;
  labels: Array<{
    text: string;
    dx: number;
    dy: number;
    fontSize: number;
    rotation?: number;
    rotationSpeed?: number;
    attachTo?: number;
  }>;
  edges: Array<[number, number]>;
};

type MousePoint = {
  x: number;
  y: number;
  active: boolean;
};

const DENSITY_POINT_COUNT = {
  low: 4,
  medium: 7,
} as const;

const EDGE_PADDING = 26;
const MOUSE_RADIUS = 130;
const MOUSE_FORCE = 0.055;
const DRIFT_FORCE = 0.00115;
const COLLISION_GAP = 14;
const LINK_ALPHA = 0.12;
const CENTER_SAFE_X = 0.24;
const CENTER_SAFE_Y = 0.24;

const CLUSTERS: ClusterTemplate[] = [
  {
    anchorX: 0.12,
    anchorY: 0.18,
    pointOffsets: [
      [0, 0],
      [38, -28],
      [56, 42],
    ],
    labels: [
      { text: "TS", dx: -10, dy: -62, fontSize: 18, rotation: -0.08, rotationSpeed: 0.00022, attachTo: 0 },
    ],
    edges: [
      [0, 1],
      [0, 2],
    ],
  },
  {
    anchorX: 0.13,
    anchorY: 0.82,
    pointOffsets: [
      [0, 0],
      [46, -20],
      [28, 58],
    ],
    labels: [
      { text: "React", dx: 8, dy: 76, fontSize: 18, rotation: 0.04, rotationSpeed: -0.00018, attachTo: 2 },
    ],
    edges: [
      [0, 1],
      [0, 2],
      [1, 2],
    ],
  },
  {
    anchorX: 0.88,
    anchorY: 0.18,
    pointOffsets: [
      [0, 0],
      [-34, 18],
      [-54, 56],
    ],
    labels: [
      { text: "Node", dx: 10, dy: -60, fontSize: 18, rotation: 0.06, rotationSpeed: 0.0002, attachTo: 0 },
    ],
    edges: [
      [0, 1],
      [1, 2],
    ],
  },
  {
    anchorX: 0.88,
    anchorY: 0.8,
    pointOffsets: [
      [0, 0],
      [-38, -18],
      [-26, 54],
    ],
    labels: [
      { text: "AWS", dx: 12, dy: 72, fontSize: 18, rotation: -0.05, rotationSpeed: -0.00016, attachTo: 2 },
    ],
    edges: [
      [0, 1],
      [1, 2],
    ],
  },
];

const createStaticBackground = (id: string) =>
  `url("data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 900' preserveAspectRatio='none'>
      <defs>
        <radialGradient id='g1' cx='18%' cy='20%' r='40%'>
          <stop offset='0%' stop-color='#efd7bd' stop-opacity='0.56' />
          <stop offset='58%' stop-color='#efd7bd' stop-opacity='0.16' />
          <stop offset='100%' stop-color='#efd7bd' stop-opacity='0' />
        </radialGradient>
        <radialGradient id='g2' cx='84%' cy='24%' r='28%'>
          <stop offset='0%' stop-color='#d5a986' stop-opacity='0.24' />
          <stop offset='100%' stop-color='#d5a986' stop-opacity='0' />
        </radialGradient>
        <radialGradient id='g3' cx='78%' cy='76%' r='32%'>
          <stop offset='0%' stop-color='#c6936c' stop-opacity='0.18' />
          <stop offset='100%' stop-color='#c6936c' stop-opacity='0' />
        </radialGradient>
        <filter id='${id}' x='-20%' y='-20%' width='140%' height='140%'>
          <feGaussianBlur stdDeviation='34' />
        </filter>
      </defs>
      <rect width='1440' height='900' fill='transparent' />
      <ellipse cx='180' cy='180' rx='260' ry='220' fill='url(%23g1)' filter='url(%23${id})' />
      <ellipse cx='1260' cy='180' rx='250' ry='180' fill='url(%23g2)' filter='url(%23${id})' />
      <ellipse cx='1180' cy='720' rx='260' ry='180' fill='url(%23g3)' filter='url(%23${id})' />
    </svg>`,
  )}")`;

const clamp = (value: number, min: number, max: number) => {
  if (value < min) return min;
  if (value > max) return max;
  return value;
};

const createPointNode = (x: number, y: number, radius = 4, alpha = 0.9): PointNode => ({
  kind: "point",
  x,
  y,
  baseX: x,
  baseY: y,
  vx: 0,
  vy: 0,
  alpha,
  radius,
  rotation: 0,
  rotationSpeed: 0,
  collisionRadius: radius + 8,
});

const createLabelNode = (
  x: number,
  y: number,
  label: string,
  fontSize: number,
  rotation = 0,
  rotationSpeed = 0.0012,
  alpha = 0.92,
): LabelNode => ({
  kind: "label",
  x,
  y,
  baseX: x,
  baseY: y,
  vx: 0,
  vy: 0,
  alpha,
  label,
  fontSize,
  rotation,
  rotationSpeed,
  collisionRadius: fontSize * 0.78,
});

const createGraph = (
  width: number,
  height: number,
  density: HeroConstellationProps["density"],
) => {
  const nodes: GraphNode[] = [];
  const edges: Array<[number, number]> = [];
  const centerMinX = width * (0.5 - CENTER_SAFE_X);
  const centerMaxX = width * (0.5 + CENTER_SAFE_X);
  const centerMinY = height * (0.5 - CENTER_SAFE_Y);
  const centerMaxY = height * (0.5 + CENTER_SAFE_Y);

  for (const cluster of CLUSTERS) {
    const anchorX = width * cluster.anchorX;
    const anchorY = height * cluster.anchorY;
    const pointIndexStart = nodes.length;

    cluster.pointOffsets.forEach(([dx, dy], index) => {
      nodes.push(createPointNode(anchorX + dx, anchorY + dy, index === 0 ? 4.8 : 4.1, 0.92));
    });

    cluster.labels.forEach((label) => {
      const labelIndex = nodes.length;

      nodes.push(
        createLabelNode(
          anchorX + label.dx,
          anchorY + label.dy,
          label.text,
          label.fontSize,
          label.rotation ?? 0,
          label.rotationSpeed ?? 0.0012,
        ),
      );

      if (typeof label.attachTo === "number") {
        edges.push([pointIndexStart + label.attachTo, labelIndex]);
      }
    });

    cluster.edges.forEach(([from, to]) => {
      edges.push([pointIndexStart + from, pointIndexStart + to]);
    });
  }

  const extraPointCount = DENSITY_POINT_COUNT[density ?? "low"];

  for (let index = 0; index < extraPointCount; index += 1) {
    let x = 0;
    let y = 0;
    let attempts = 0;

    while (attempts < 24) {
      attempts += 1;
      x = EDGE_PADDING + Math.random() * Math.max(width - EDGE_PADDING * 2, 1);
      y = EDGE_PADDING + Math.random() * Math.max(height - EDGE_PADDING * 2, 1);

      const isInsideTightCenter =
        x > centerMinX && x < centerMaxX && y > centerMinY && y < centerMaxY;

      if (!isInsideTightCenter || Math.random() > 0.65) {
        break;
      }
    }

    nodes.push(createPointNode(x, y, 1.8 + Math.random() * 1.1, 0.18 + Math.random() * 0.12));
  }

  return { nodes, edges };
};

const HeroConstellation: React.FC<HeroConstellationProps> = ({
  className,
  density = "medium",
  minWidth = 768,
  motionScale = 1,
  opacity = 1,
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationFrameRef = useRef<number>();
  const nodesRef = useRef<GraphNode[]>([]);
  const edgesRef = useRef<Array<[number, number]>>([]);
  const mouseRef = useRef<MousePoint>({ x: 0, y: 0, active: false });
  const [isCanvasReady, setIsCanvasReady] = useState(true);
  const [isVisibleViewport, setIsVisibleViewport] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  const backgroundId = useId().replace(/:/g, "");
  const staticBackgroundImage = useMemo(
    () => createStaticBackground(`hero-constellation-blur-${backgroundId}`),
    [backgroundId],
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleResize = () => {
      setIsVisibleViewport(window.innerWidth >= minWidth);
    };

    handleResize();
    window.addEventListener("resize", handleResize, { passive: true });

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [minWidth]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;

    if (!canvas || !wrapper || prefersReducedMotion || !isVisibleViewport || !isCanvasReady) {
      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = undefined;
      }

      return undefined;
    }

    const context = canvas.getContext("2d");

    if (!context) {
      setIsCanvasReady(false);
      return undefined;
    }

    let disposed = false;

    const resizeCanvas = () => {
      const bounds = wrapper.getBoundingClientRect();
      const width = Math.round(bounds.width);
      const height = Math.round(bounds.height);
      const devicePixelRatio = window.devicePixelRatio || 1;

      if (!width || !height) {
        return;
      }

      canvas.width = Math.round(width * devicePixelRatio);
      canvas.height = Math.round(height * devicePixelRatio);
      context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);

      const graph = createGraph(width, height, density);
      nodesRef.current = graph.nodes;
      edgesRef.current = graph.edges;
    };

    const handleMouseMove = (event: MouseEvent) => {
      const bounds = wrapper.getBoundingClientRect();
      const isInside =
        event.clientX >= bounds.left &&
        event.clientX <= bounds.right &&
        event.clientY >= bounds.top &&
        event.clientY <= bounds.bottom;

      if (!isInside) {
        mouseRef.current.active = false;
        return;
      }

      mouseRef.current = {
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
        active: true,
      };
    };

    const handleMouseLeaveViewport = () => {
      mouseRef.current.active = false;
    };

    const drawFrame = () => {
      if (disposed) {
        return;
      }

      const width = canvas.width / (window.devicePixelRatio || 1);
      const height = canvas.height / (window.devicePixelRatio || 1);
      const nodes = nodesRef.current;
      const edges = edgesRef.current;

      context.clearRect(0, 0, width, height);

      for (const node of nodes) {
        const driftX = (node.baseX - node.x) * DRIFT_FORCE * motionScale;
        const driftY = (node.baseY - node.y) * DRIFT_FORCE * motionScale;

        node.vx += driftX;
        node.vy += driftY;

        if (mouseRef.current.active) {
          const deltaX = node.x - mouseRef.current.x;
          const deltaY = node.y - mouseRef.current.y;
          const distance = Math.hypot(deltaX, deltaY);

          if (distance < MOUSE_RADIUS && distance > 0.1) {
            const force = (1 - distance / MOUSE_RADIUS) * MOUSE_FORCE * motionScale;
            node.vx += (deltaX / distance) * force;
            node.vy += (deltaY / distance) * force;
          }
        }

        node.vx *= 0.984;
        node.vy *= 0.984;
        node.x += node.vx;
        node.y += node.vy;
        node.rotation += node.rotationSpeed * motionScale;

        node.x = clamp(node.x, EDGE_PADDING, width - EDGE_PADDING);
        node.y = clamp(node.y, EDGE_PADDING, height - EDGE_PADDING);
      }

      for (let index = 0; index < nodes.length; index += 1) {
        const node = nodes[index];

        for (let innerIndex = index + 1; innerIndex < nodes.length; innerIndex += 1) {
          const comparison = nodes[innerIndex];
          const deltaX = comparison.x - node.x;
          const deltaY = comparison.y - node.y;
          const distance = Math.hypot(deltaX, deltaY);
          const minDistance = node.collisionRadius + comparison.collisionRadius + COLLISION_GAP;

          if (distance === 0 || distance >= minDistance) {
            continue;
          }

          const push = (minDistance - distance) / minDistance * 0.16;
          const pushX = (deltaX / distance) * push;
          const pushY = (deltaY / distance) * push;

          node.x -= pushX;
          node.y -= pushY;
          comparison.x += pushX;
          comparison.y += pushY;
        }
      }

      for (const [fromIndex, toIndex] of edges) {
        const from = nodes[fromIndex];
        const to = nodes[toIndex];
        const distance = Math.hypot(to.x - from.x, to.y - from.y);
        const lineStrength = clamp(1 - distance / 170, 0.16, 1);

        context.beginPath();
        context.moveTo(from.x, from.y);
        context.lineTo(to.x, to.y);
        context.strokeStyle = `rgba(171, 112, 76, ${LINK_ALPHA * lineStrength * opacity})`;
        context.lineWidth = 1;
        context.shadowBlur = 6;
        context.shadowColor = `rgba(213, 169, 134, ${0.12 * opacity})`;
        context.stroke();
        context.shadowBlur = 0;
      }

      for (const node of nodes) {
        if (node.kind === "point") {
          context.beginPath();
          context.fillStyle = `rgba(171, 112, 76, ${node.alpha * opacity})`;
          context.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
          context.shadowBlur = 8;
          context.shadowColor = `rgba(213, 169, 134, ${0.12 * opacity})`;
          context.fill();
          context.shadowBlur = 0;
          continue;
        }

        context.save();
        context.translate(node.x, node.y);
        context.rotate(node.rotation);
        context.fillStyle = `rgba(150, 98, 66, ${node.alpha * opacity})`;
        context.font = `700 ${node.fontSize}px "Avenir Next", "Trebuchet MS", sans-serif`;
        context.textAlign = "center";
        context.textBaseline = "middle";
        context.shadowBlur = 8;
        context.shadowColor = `rgba(235, 190, 150, ${0.12 * opacity})`;
        context.fillText(node.label, 0, 0);
        context.restore();
        context.shadowBlur = 0;
      }

      animationFrameRef.current = window.requestAnimationFrame(drawFrame);
    };

    resizeCanvas();
    drawFrame();

    const resizeObserver = new ResizeObserver(resizeCanvas);
    resizeObserver.observe(wrapper);
    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    window.addEventListener("mouseleave", handleMouseLeaveViewport);

    return () => {
      disposed = true;
      resizeObserver.disconnect();
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseleave", handleMouseLeaveViewport);

      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = undefined;
      }
    };
  }, [density, isCanvasReady, isVisibleViewport, motionScale, opacity, prefersReducedMotion]);

  const shouldAnimate = isCanvasReady && isVisibleViewport && !prefersReducedMotion;

  return (
    <div
      ref={wrapperRef}
      aria-hidden="true"
      className={className}
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 0,
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: staticBackgroundImage,
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          opacity: shouldAnimate ? 0.24 : 0.34,
          transition: "opacity 180ms ease",
        }}
      />
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          opacity: shouldAnimate ? opacity : 0,
          pointerEvents: "none",
          transition: "opacity 220ms ease",
        }}
      />
    </div>
  );
};

export default HeroConstellation;
